import React from 'react';
import PropTypes from 'prop-types';

/**
 * Error Boundary component to catch JavaScript errors in child components.
 * Prevents errors from crashing the entire page and displays a fallback UI.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  componentDidCatch(error, errorInfo) {
    // Log error to console in development
    // In production, you could send this to an error reporting service
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    const { hasError } = this.state;
    const { children, fallback, componentName } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <div
          style={{
            padding: '20px',
            backgroundColor: '#fff3f3',
            border: '1px solid #ffcdd2',
            borderRadius: '4px',
            textAlign: 'center',
          }}
        >
          <p style={{ color: '#c62828', marginBottom: '10px', fontSize: '14px' }}>
            {componentName
              ? `Une erreur est survenue lors du chargement de ${componentName}.`
              : 'Une erreur est survenue lors du chargement de ce composant.'}
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            style={{
              padding: '8px 16px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Réessayer
          </button>
        </div>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  componentName: PropTypes.string,
};

ErrorBoundary.defaultProps = {
  fallback: null,
  componentName: null,
};

export default ErrorBoundary;
